import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import ConfigureTab from "@/components/assistentes/tabs/ConfigureTab";
import PromptTab from "@/components/assistentes/tabs/PromptTab";
import KnowledgeBaseTab from "@/components/assistentes/tabs/KnowledgeBaseTab";
import IntegrationsTab from "@/components/assistentes/tabs/IntegrationsTab/IntegrationsTab";
import WebhooksTab from "@/components/assistentes/tabs/WebhooksTab";
import FollowUpTab from "@/components/assistentes/tabs/FollowUpTab";
import AssistantSimulationChat from "@/components/assistentes/AssistantSimulationChat";
import { Button } from "@/components/ui/button";
import { useAssistants } from "@/hooks/useAssistants";

const AssistenteEditar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { assistants, updateAssistant } = useAssistants();

  const [activeTab, setActiveTab] = useState("configure");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const assistant = assistants.find(a => a.id === id);

  const [formData, setFormData] = useState({
    name: '',
    model: 'gpt-4o',
    response_time_value: 1,
    response_time_unit: 'seconds',
    max_messages: 1,
    prompt_mode: 'guided-mode',
    default_prompt: '',
    voice_tone: '',
    main_objective: '',
    language_rules: '',
    good_examples: '',
    restrictions: '',
    additional_info: '',
    is_active: true,
    disable_assistant_on_manual_message: false,
  });

  useEffect(() => {
    if (assistant) {
      setFormData({
        name: assistant.name,
        model: assistant.model,
        response_time_value: assistant.response_time_value,
        response_time_unit: 'seconds',
        max_messages: assistant.max_messages,
        prompt_mode: assistant.prompt_mode,
        default_prompt: assistant.default_prompt || '',
        voice_tone: assistant.voice_tone || '',
        main_objective: assistant.main_objective || '',
        language_rules: assistant.language_rules || '',
        good_examples: assistant.good_examples || '',
        restrictions: assistant.restrictions || '',
        additional_info: assistant.additional_info || '',
        is_active: assistant.is_active,
        disable_assistant_on_manual_message: assistant.disable_assistant_on_manual_message ?? false,
      });
    }
  }, [assistant]);

  const handleFormChange = (field: string, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setMessage({ type: 'error', text: 'Nome do assistente é obrigatório' });
      return;
    }

    if (!id) return;

    setLoading(true);
    setMessage(null);

    try {
      const success = await updateAssistant(id, {
        ...formData,
        response_time_unit: 'seconds',
      });

      if (success) {
        setMessage({ type: 'success', text: 'Assistente atualizado com sucesso!' });
        setTimeout(() => {
          setMessage(null);
        }, 3000);
      } else {
        setMessage({ type: 'error', text: 'Falha ao atualizar assistente' });
      }
    } catch (error) {
      console.error('Erro ao atualizar assistente:', error);
      setMessage({ type: 'error', text: 'Erro ao atualizar assistente' });
    } finally {
      setLoading(false);
    }
  };

  if (!assistant) {
    return (
      <div className="w-full h-[calc(100dvh-110px)] flex items-center justify-center bg-[#F8FAFC] dark:bg-transparent">
        <div className="text-center">
          <div className="w-16 h-16 bg-[#F1F5F9] rounded-full flex items-center justify-center mx-auto mb-4 dark:bg-neutral-600">
            <svg className="w-8 h-8 text-[#64748B] dark:text-neutral-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.732-.833-2.464 0L4.35 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-[#0F172A] mb-2 dark:text-neutral-50">Assistente não encontrado</h3>
          <button
            onClick={() => navigate('/assistentes')}
            className="mt-4 px-4 py-2 bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl text-white transition-colors duration-200"
          >
            Voltar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full h-[calc(100dvh-110px)] flex flex-col">
      <div className="mb-4 flex items-center gap-4">
        <button
          onClick={() => navigate('/assistentes')}
          className="flex items-center gap-2 rounded-xl px-2 py-1.5 text-[#334155] hover:bg-[#F1F5F9] hover:text-[#2563EB] transition-colors duration-200 dark:text-[#D4D4D8] dark:hover:bg-[#27272A] dark:hover:text-[#60A5FA]"
        >
          <ArrowLeft size={20} />
          <span>Voltar</span>
        </button>
        <h2 className="text-2xl xl:text-3xl font-bold tracking-tight text-[#0F172A] dark:text-[#FAFAFA]">
          Editar Assistente: {assistant.name}
        </h2>
      </div>

      {message && (
        <div className={`p-3 rounded-xl mb-4 border ${
          message.type === 'success'
            ? 'bg-[#ECFDF5] border-green-200 text-[#16A34A] dark:bg-green-500/10 dark:border-green-500/30 dark:text-[#22C55E]'
            : 'bg-[#FEF2F2] border-red-200 text-[#DC2626] dark:bg-red-500/10 dark:border-red-500/30 dark:text-[#F87171]'
        }`}>
          {message.text}
        </div>
      )}

      <div className="flex-1 min-h-0 grid grid-cols-1 lg:grid-cols-12 gap-4 xl:gap-6">
        <div className="lg:col-span-8 flex flex-col min-h-0">
          <div className="flex-1 min-h-0 flex flex-col overflow-hidden rounded-2xl border border-[#E5E7EB] bg-white p-4 md:p-5 shadow-sm dark:border-[#3F3F464D] dark:bg-[#20202040] dark:shadow-none">
            <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col gap-4 min-h-0">
              <TabsList className="mb-0 shrink-0">
                <TabsTrigger value="configure">Configure</TabsTrigger>
                <TabsTrigger value="prompt">Prompt</TabsTrigger>
                <TabsTrigger value="knowledge-base">Base de conhecimento</TabsTrigger>
                <TabsTrigger value="integrations">Integrações</TabsTrigger>
                <TabsTrigger value="webhooks">Webhooks</TabsTrigger>
                <TabsTrigger value="follow-up">Follow-Up</TabsTrigger>
              </TabsList>

              <div className="flex-1 overflow-y-auto min-h-0">
                <TabsContent value="configure" className="mt-0">
                  <ConfigureTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="prompt" className="mt-0">
                  <PromptTab formData={formData} onFormChange={handleFormChange} />
                </TabsContent>
                <TabsContent value="knowledge-base" className="mt-0">
                  <KnowledgeBaseTab />
                </TabsContent>
                <TabsContent value="integrations" className="mt-0">
                  {id && <IntegrationsTab assistantId={id} />}
                </TabsContent>
                <TabsContent value="webhooks" className="mt-0">
                  <WebhooksTab />
                </TabsContent>
                <TabsContent value="follow-up" className="mt-0">
                  <FollowUpTab />
                </TabsContent>
              </div>
            </Tabs>

            {(activeTab === "configure" || activeTab === "prompt") && (
              <div className="mt-4 pt-4 border-t border-[#E5E7EB] dark:border-[#3F3F464D]">
                <Button
                  onClick={handleSave}
                  disabled={loading}
                  className="w-[120px] rounded-xl bg-[#2563EB] text-white shadow-sm hover:bg-[#1D4ED8] transition-colors duration-200 dark:bg-[#2563EB] dark:hover:bg-[#1D4ED8]"
                >
                  {loading ? 'Salvando...' : 'Salvar'}
                </Button>
              </div>
            )}
          </div>
        </div>

        <div className="lg:col-span-4 h-full min-h-[500px] lg:min-h-0">
          <AssistantSimulationChat assistant={{
            ...assistant,
            ...formData
          }} />
        </div>
      </div>
    </div>
  );
};

export default AssistenteEditar;
